<template>
	<section class="pc-energy" :class="{ 'pc-energy--en': locale === 'en' }">
		<div v-if="dataLoading" class="pc-center-1200 energy-skeleton" aria-hidden="true">
			<section v-for="card in 2" :key="card" class="energy-skeleton__card">
				<span class="energy-skeleton__title"></span>
				<div class="energy-skeleton__body">
					<span class="energy-skeleton__circle"></span>
					<div><span v-for="line in 4" :key="line" class="energy-skeleton__line"></span></div>
				</div>
			</section>
		</div>
		<div v-show="!dataLoading" class="pc-center-1200 energy-content">
			<div ref="dailyEnergyRef" class="left">
				<div class="title">
					<img src="../../../images/pc/title-left.png" class="pic" alt="" />
					{{ t("index.energy.title") }}
					<img src="../../../images/pc/title-right.png" class="pic" alt="" />
				</div>
				<div class="content">
					<div class="box1">
						<div class="gauge-wrap">
							<svg class="gauge" :key="gaugeRenderKey" viewBox="0 0 220 130" aria-hidden="true">
								<g
									v-for="(seg, index) in gaugeSegments"
									:key="index"
									:transform="`rotate(${seg.rotate}, ${seg.cx}, ${seg.cy})`"
								>
									<rect
										:x="seg.x"
										:y="seg.y"
										width="9"
										height="22"
										rx="2"
										fill="#F5EBD4"
										:class="{ animate: animateReady && seg.active }"
										:style="{ animationDelay: `${index * 0.06}s` }"
									/>
								</g>
							</svg>
							<div class="gauge-value" :class="{ animate: animateReady }">{{ energyValue }}</div>
						</div>
						<!-- <div class="energy-label">{{ t("index.energy.todayLabel") }}</div> -->
						<p class="energy-tip" :class="{ animate: animateReady, 'energy-tip--login': isLogin }">
							<span>{{ energyDesc }}</span>
						</p>
					</div>
					<div class="line"></div>
					<div class="box2" :class="{ 'box2--guest': !isLogin }">
						<div class="box2-inner">
							<template v-if="showEnergyCharts">
								<div class="energy-charts">
									<div v-for="item in scoreList" :key="item.title" class="chart-item">
										<div class="chart-bar-wrap">
											<div
												class="chart-bar"
												:style="{ height: `${item.score}%` }"
												:class="{ animate: animateReady }"
											></div>
										</div>
										<span class="chart-score">{{ item.score }}</span>
										<span class="chart-title">{{ item.title }}</span>
									</div>
								</div>
							</template>
							<template v-else>
								<div class="energy-lock">
									<img src="../../../images/pc/mohu.png" class="energy-lock-img" alt="" />
									<p class="energy-lock-text">{{ energyLockText }}</p>
								</div>
								<div class="energy-categories">
									<span v-for="item in categoryList" :key="item">{{ item }}</span>
								</div>
							</template>
							<div class="energy-detail-link" @click="handleEnergyDetailClick">
								<span>{{ t("index.energy.viewDetail") }}</span>
								<img src="../../../images/pc/guide.png" class="guide-arrow" alt="" />
							</div>
						</div>
					</div>
				</div>
			</div>
			<div ref="almanacRef" class="right">
				<div class="ts-title">
					<img src="../../../images/pc/title-left.png" class="pic" alt="" />
					{{ t("index.energy.ts.title") }}
					<img src="../../../images/pc/title-right.png" class="pic" alt="" />
				</div>
				<div class="ts-body">
					<div class="ts-date" :class="{ animate: animateReady }">
						<div class="top">
							<p class="day">{{ tsData.date }}</p>
							<p class="week">{{ tsData.week }}</p>
						</div>
						<p class="lunar" :class="{ 'lunar--en': locale === 'en' }">{{ tsData.lunar }}</p>
						<div class="ganzhi" :class="{ 'ganzhi--en': locale === 'en' }">
							<div
								v-for="(item, index) in tsData.ganzhiItems"
								:key="item.label || index"
								class="ganzhi-item"
							>
								<span class="ganzhi-value">
									<template v-if="item.parts">
										<span v-for="(part, i) in item.parts" :key="i">{{ part }}</span>
									</template>
									<template v-else>{{ item.value }}</template>
								</span>
								<span v-if="item.label" class="ganzhi-label">{{ item.label }}</span>
							</div>
						</div>
					</div>
					<div class="ts-line"></div>
					<div class="ts-info">
						<div class="row good">
							<span class="tag">{{ t("index.energy.ts.good") }}</span>
							<p class="text" :class="{ animate: animateReady }">{{ tsData.good }}</p>
						</div>
						<div class="row bad">
							<span class="tag">{{ t("index.energy.ts.bad") }}</span>
							<p class="text" :class="{ animate: animateReady }">{{ tsData.bad }}</p>
						</div>
						<div class="ts-link" @click="handleAlmanacDetailClick">
							<span>{{ t("index.energy.ts.viewAll") }}</span>
							<img src="../../../images/pc/guide.png" class="arrow" alt="" />
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup>
import { computed, onMounted, ref, watch, nextTick, inject } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "~/composables/useI18n";
import { useLangImg } from "~/composables/useLangImg";
import { useUserStore } from "~/stores/user";
import { getHomeCalendar, getHomeEnergy } from "~/api/api";
import {
	useHomepageTracking,
	useSectionView,
} from "@variants/index/composables/useHomepageTracking";
const { t, locale } = useI18n();
const { isLogin } = storeToRefs(useUserStore());
const { track } = useHomepageTracking("pc");
const openLoginPopup = inject("openLoginPopup", null);

const dailyEnergyRef = ref(null);
const almanacRef = ref(null);

const energyData = ref(null);
const calendarData = ref(null);
const dataLoading = ref(true);
const profileMissing = ref(false);
// 动画开关
const animateReady = ref(false);
const gaugeRenderKey = ref(0);

const getLangImg = useLangImg(
	import.meta.glob("../../../images/pc/*/*.{png,jpg,jpeg,webp,svg,gif}", {
		eager: true,
		import: "default",
	})
);

const categoryList = computed(() => [
	t("index.energy.categories.emotion"),
	t("index.energy.categories.career"),
	t("index.energy.categories.wealth"),
	t("index.energy.categories.travel"),
	t("index.energy.categories.health"),
]);

const totalScore = computed(() => energyData.value?.yunShiTotalScore?.score ?? 0);

/** 已登录且有档案时才展示分项能量图 */
const showEnergyCharts = computed(() => isLogin.value && !profileMissing.value);

const energyLockText = computed(() =>
	profileMissing.value ? t("index.energy.addProfile") : t("index.energy.mohu")
);

const energyValue = computed(() => {
	if (!isLogin.value || profileMissing.value) return "??";
	return totalScore.value || "--";
});

const energyDesc = computed(() => {
	if (!isLogin.value || profileMissing.value) return "";
	const desc = energyData.value?.yunShiTotalScore?.desc;
	if (Array.isArray(desc)) return desc.join("");
	return desc || "";
});

const scoreList = computed(() => {
	if (!showEnergyCharts.value) return [];
	return energyData.value?.yunShiScore || [];
});

const formatSolarDate = (solar, lang) => {
	const month = String(solar.month).padStart(2, "0");
	const day = String(solar.day).padStart(2, "0");

	if (lang === "en") {
		const date = new Date(solar.year, solar.month - 1, solar.day);
		return new Intl.DateTimeFormat("en-US", {
			year: "numeric",
			month: "long",
			day: "2-digit",
		}).format(date);
	}

	return `${solar.year}年 ${month}月${day}日`;
};

const tsData = computed(() => {
	const riLi = calendarData.value?.riLi;
	if (!riLi) {
		return {
			date: "",
			week: "",
			lunar: "",
			ganzhiItems: [],
			good: "",
			bad: "",
		};
	}

	const { solar, lunar, baZi, weekL, weekEn, yi, ji } = riLi;
	const isEn = locale.value === "en";

	return {
		date: formatSolarDate(solar, locale.value),
		week: isEn ? weekEn : weekL,
		lunar: `${lunar.month}${lunar.day}`,
		ganzhiItems: isEn
			? [
					{
						label: t("index.energy.ts.ganzhi.year"),
						value: baZi.year,
						parts: String(baZi.year || "")
							.split(/\s+/)
							.filter(Boolean),
					},
					{
						label: t("index.energy.ts.ganzhi.month"),
						value: baZi.month,
						parts: String(baZi.month || "")
							.split(/\s+/)
							.filter(Boolean),
					},
					{
						label: t("index.energy.ts.ganzhi.day"),
						value: baZi.day,
						parts: String(baZi.day || "")
							.split(/\s+/)
							.filter(Boolean),
					},
				]
			: [{ value: `${baZi.year}年` }, { value: `${baZi.month}月` }, { value: `${baZi.day}日` }],
		good: (yi || []).join(" "),
		bad: (ji || []).join(" "),
	};
});

const getTodayYmd = (date = new Date()) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}${month}${day}`;
};

const almanacDate = computed(() => getTodayYmd());

useSectionView(dailyEnergyRef, {
	track,
	sectionId: "daily_energy",
	getExtraParams: () => {
		if (!isLogin.value) return {};
		const score = totalScore.value;
		return score > 0 ? { energy_score: score } : {};
	},
});

useSectionView(almanacRef, {
	track,
	sectionId: "almanac",
	getExtraParams: () => ({ almanac_date: almanacDate.value }),
});

const handleEnergyDetailClick = () => {
	track("energy_detail_click", {
		element_id: "energy_detail",
		...(isLogin.value && totalScore.value > 0 ? { energy_score: totalScore.value } : {}),
	});

	if (!isLogin.value) {
		if (typeof openLoginPopup === "function") {
			openLoginPopup(() => {
				navigateTo("/seer/daily-energy");
			});
			return;
		}
		navigateTo("/seer/login");
		return;
	}

	if (profileMissing.value) {
		navigateTo("/seer/user?type=default");
		return;
	}

	navigateTo("/seer/daily-energy");
};

const handleAlmanacDetailClick = () => {
	track("almanac_detail_click", {
		element_id: "almanac_detail",
		almanac_date: almanacDate.value,
	});
	navigateTo("/seer/huangli");
};

const getTsData = async () => {
	try {
		const res = await getHomeCalendar({ date: getTodayYmd() });
		calendarData.value = res?.data || null;
	} catch (error) {
		console.error("獲取通勝數據失敗:", error);
	}
};

const getEnergyData = async () => {
	if (!isLogin.value) {
		energyData.value = null;
		profileMissing.value = false;
		return;
	}

	try {
		const res = await getHomeEnergy();
		energyData.value = res?.data || null;
		profileMissing.value = false;
	} catch (error) {
		console.error("獲取今日能量數據失敗:", error);
		energyData.value = null;
		profileMissing.value = error?.code === 1303;
	}
};

onMounted(async () => {
	try {
		await Promise.all([getTsData(), getEnergyData()]);
		await nextTick();
		animateReady.value = true;
	} finally {
		dataLoading.value = false;
	}
});

// 语言/登录切换重置动画
watch([locale, isLogin], async () => {
	dataLoading.value = true;
	animateReady.value = false;
	gaugeRenderKey.value += 1;
	try {
		await Promise.all([getTsData(), getEnergyData()]);
		await nextTick();
		animateReady.value = true;
	} finally {
		dataLoading.value = false;
	}
});

const SEGMENT_COUNT = 22;

const gaugeActiveCount = computed(() => {
	if (!isLogin.value) return 15;
	if (profileMissing.value) return 0;
	return Math.round(SEGMENT_COUNT * (totalScore.value / 100));
});

const gaugeSegments = computed(() => {
	const cx = 110;
	const cy = 110;
	const radius = 85;
	const segW = 9;
	const segH = 22;
	const startAngle = Math.PI;
	const endAngle = Math.PI * 2;
	const activeCount = gaugeActiveCount.value;

	return Array.from({ length: SEGMENT_COUNT }, (_, index) => {
		const ratio = index / (SEGMENT_COUNT - 1);
		const angle = startAngle + ratio * (endAngle - startAngle);
		const pivotX = cx + radius * Math.cos(angle);
		const pivotY = cy + radius * Math.sin(angle);

		return {
			x: pivotX - segW / 2,
			y: pivotY - segH / 2,
			cx: pivotX,
			cy: pivotY,
			rotate: (angle * 180) / Math.PI + 90,
			active: index < activeCount,
		};
	});
});
</script>

<style lang="scss" scoped>
// 动画关键帧
@keyframes numScaleUp {
	0% {
		opacity: 0;
		transform: translateX(-50%) scale(0.6);
	}
	100% {
		opacity: 1;
		transform: translateX(-50%) scale(1);
	}
}
@keyframes fadeIn {
	0% {
		opacity: 0;
	}
	100% {
		opacity: 1;
	}
}
@keyframes barRise {
	0% {
		transform: translateY(100%);
	}
	100% {
		transform: translateY(0);
	}
}
@keyframes segLightUp {
	0% {
		fill: #f5ebd4;
	}
	100% {
		fill: #f0a820;
	}
}

.energy-skeleton {
	display: grid;
	// 与真实首页内容一致：左侧今日能量更宽，右侧通胜更窄。
	grid-template-columns: minmax(0, 3.5fr) minmax(0, 2fr);
	gap: 20px;
}
.energy-skeleton__card {
	min-height: 360px;
	padding: 30px;
	border-radius: 20px;
	box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
	box-sizing: border-box;
}
.energy-skeleton__title,
.energy-skeleton__circle,
.energy-skeleton__line {
	display: block;
	background: linear-gradient(90deg, #eee 25%, #fafafa 42%, #eee 62%);
	background-size: 300% 100%;
	animation: home-energy-shimmer 1.35s ease-in-out infinite;
}
.energy-skeleton__title {
	width: 180px;
	height: 30px;
	margin: 0 auto 42px;
	border-radius: 6px;
}
.energy-skeleton__body {
	display: flex;
	align-items: center;
	gap: 38px;
}
.energy-skeleton__circle {
	flex: 0 0 150px;
	width: 150px;
	height: 150px;
	border-radius: 50%;
}
.energy-skeleton__body > div {
	flex: 1;
}
.energy-skeleton__line {
	height: 18px;
	margin: 20px 0;
	border-radius: 5px;
}
.energy-skeleton__line:nth-child(2) {
	width: 78%;
}
.energy-skeleton__line:nth-child(3) {
	width: 62%;
}
.energy-skeleton__line:nth-child(4) {
	width: 84%;
}
@keyframes home-energy-shimmer {
	to {
		background-position: -100% 0;
	}
}

.pc-energy {
	background: #ffffff;
	padding: 20px 0;

	.energy-content {
		display: flex;
		gap: 20px;

		.left {
			flex: 3.5;
			min-width: 0;
			box-shadow: 0px 4px 24.6px 0px rgba(0, 0, 0, 0.1);
			background-color: #ffffff;
			padding: 20px 30px;
			border-radius: 20px;

			.title {
				display: flex;
				justify-content: center;
				align-items: center;
				gap: 10px;
				font-size: 24px;
				color: #333333;
				font-weight: bold;

				.pic {
					width: 60px;
					height: 8px;
				}
			}

			.content {
				display: flex;
				margin-top: 24px;

				.box1 {
					flex: 1;
					display: flex;
					flex-direction: column;
					align-items: center;
					justify-content: center;
					text-align: center;

					.gauge-wrap {
						position: relative;
						width: 160px;
						height: 88px;
						margin-bottom: 8px;

						.gauge {
							display: block;
							width: 100%;
							height: auto;
							rect {
								&.animate {
									animation: segLightUp 0.35s ease-out forwards;
								}
							}
						}

						.gauge-value {
							position: absolute;
							left: 50%;
							bottom: 0;
							transform: translateX(-50%);
							font-family:
								"Source Han Sans CN", "PingFang SC", "Microsoft YaHei", sans-serif !important;
							font-size: 38px;
							font-weight: 700;
							line-height: 1;
							color: #333333;
							letter-spacing: 2px;
							opacity: 0;
							&.animate {
								animation: numScaleUp 0.8s ease-out forwards;
							}
						}
					}

					.energy-label {
						display: inline-flex;
						align-items: center;
						justify-content: center;
						min-width: 96px;
						padding: 4px 18px;
						border: 1px solid #f2e5b5;
						border-radius: 999px;
						background: #ffffff;
						font-size: 20px;
						color: #333333;
						margin-top: 5px;
						font-weight: bold;
					}

					.energy-tip {
						margin: 16px 0 0;
						max-width: 300px;
						font-size: 16px;
						line-height: 24px;
						color: #333333;
						text-align: left;
						opacity: 0;
						display: flex;
						align-items: center;
						span {
							display: -webkit-box;
							-webkit-line-clamp: 4;
							line-clamp: 4;
							-webkit-box-orient: vertical;
							overflow: hidden;
							text-overflow: ellipsis;
						}
						&.animate {
							animation: fadeIn 0.7s ease-out 0.4s forwards;
						}
						&--login {
							min-height: 96px;
						}
					}
				}

				.line {
					width: 1px;
					background: #ffe9d9;
					margin: 0 20px;
				}

				.box2 {
					flex: 1;
					display: flex;
					justify-content: center;
					align-items: center;
					padding-top: 38px;

					&--guest {
						padding-top: 0;
					}

					.box2-inner {
						width: 100%;
						max-width: 378px;
						display: flex;
						flex-direction: column;
					}

					.energy-lock {
						position: relative;
						width: 100%;
						max-width: 378px;
						height: 102px;
						.energy-lock-img {
							width: 100%;
							height: 100%;
						}

						.energy-lock-text {
							width: 100%;
							position: absolute;
							top: 50%;
							left: 50%;
							transform: translate(-50%, -50%);
							font-size: 20px;
							line-height: 26px;
							color: #333;
							text-align: center;
						}
					}

					.energy-profile-tip {
						display: flex;
						align-items: center;
						justify-content: center;
						width: 100%;
						max-width: 378px;
						min-height: 102px;

						p {
							margin: 0;
							font-size: 20px;
							line-height: 26px;
							color: #333333;
							font-weight: bold;
							text-align: center;
						}
					}

					.energy-charts {
						display: flex;
						justify-content: space-between;
						align-items: flex-end;
						width: 100%;
						max-width: 378px;
						height: 102px;
						padding: 0 5px;

						.chart-item {
							display: flex;
							flex-direction: column;
							align-items: center;
							flex: 1;
						}

						.chart-bar-wrap {
							width: 12px;
							height: 72px;
							display: flex;
							align-items: flex-end;
							background: #f3ecd4;
							border-radius: 999px;
							overflow: hidden;
						}

						.chart-bar {
							width: 100%;
							display: flex;
							justify-content: center;
							padding-top: 6px;
							border-radius: 999px;
							background: linear-gradient(to bottom, #f2af19 0%, #ffe790 100%);
							min-height: 28px;
							transform: translateY(100%);
							&.animate {
								animation: barRise 0.9s ease-out 0.2s forwards;
							}
						}

						.chart-score {
							font-size: 20px;
							line-height: 1;
							color: #333333;
							margin-top: 10px;
						}

						.chart-title {
							margin-top: 8px;
							font-size: 20px;
							line-height: 22px;
							color: #333333;
						}
					}

					.energy-categories {
						display: flex;
						justify-content: space-between;
						margin-top: 12px;
						font-size: 20px;
						line-height: 22px;
						color: #333;
						padding: 0 5px;
					}

					.energy-detail-link {
						width: 100%;
						display: inline-flex;
						align-items: center;
						justify-content: center;
						gap: 8px;
						align-self: flex-end;
						margin-top: 40px;
						font-size: 18px;
						line-height: 26px;
						color: #333333;
						text-decoration: none;
						cursor: pointer;

						&:hover {
							opacity: 0.8;
						}

						.guide-arrow {
							width: 26px;
							height: 26px;
							flex-shrink: 0;
						}
					}
				}
			}
		}

		.right {
			flex: 2;
			min-width: 0;
			box-shadow: 0px 4px 24.6px 0px rgba(0, 0, 0, 0.1);
			background-color: #ffffff;
			padding: 20px 30px;
			border-radius: 20px;

			.ts-title {
				display: flex;
				justify-content: center;
				align-items: center;
				gap: 10px;
				font-size: 24px;
				color: #333333;
				font-weight: bold;

				.pic {
					width: 60px;
					height: 8px;
				}
			}

			.ts-body {
				display: flex;
				margin-top: 24px;

				.ts-date {
					flex: 1;
					display: flex;
					flex-direction: column;
					align-items: center;
					justify-content: space-between;
					padding-right: 24px;
					opacity: 0;
					&.animate {
						animation: fadeIn 0.7s ease-out 0.3s forwards;
					}

					.top {
						text-align: center;
						.day {
							margin: 0;
							font-size: 18px;
							line-height: 24px;
							color: #333333;
						}
						.week {
							margin: 0;
							font-size: 18px;
							line-height: 24px;
							color: #333333;
						}
					}

					.lunar {
						margin: 24px 0;
						font-size: 28px;
						line-height: 1.2;
						color: #333333;
						text-align: center;

						&--en {
							font-size: 22px !important;
						}
					}

					.ganzhi {
						display: flex;
						justify-content: center;
						gap: 30px;

						.ganzhi-value {
							writing-mode: vertical-rl;
							font-size: 20px;
							line-height: 1;
							color: #333333;
							letter-spacing: 2px;
						}

						&--en {
							justify-content: center;
							gap: 24px;

							.ganzhi-item {
								display: flex;
								flex-direction: column;
								align-items: center;
								justify-content: center;
								gap: 6px;
								text-align: center;
							}

							.ganzhi-value {
								writing-mode: horizontal-tb;
								display: flex;
								flex-direction: column;
								align-items: center;
								font-size: 16px;
								line-height: 1.25;
								letter-spacing: 0;
								text-align: center;
							}

							.ganzhi-label {
								font-size: 14px;
								line-height: 1;
								color: #888888;
								text-align: center;
							}
						}
					}
				}

				.ts-line {
					width: 1px;
					background: #ffe9d9;
					flex-shrink: 0;
				}

				.ts-info {
					flex: 1;
					justify-content: center;
					display: flex;
					flex-direction: column;
					padding-left: 24px;

					.row {
						display: flex;
						align-items: flex-start;
						gap: 12px;
						& + .row {
							margin-top: 20px;
						}

						.tag {
							flex-shrink: 0;
							width: 46px;
							height: 46px;
							border-radius: 50%;
							display: flex;
							align-items: center;
							justify-content: center;
							font-size: 24px;
							color: #ffffff;
							font-weight: bold;
						}

						.text {
							margin: 0;
							flex: 1;
							font-size: 18px;
							line-height: 26px;
							color: #333333;
							opacity: 0;
							display: -webkit-box;
							-webkit-line-clamp: 2;
							-webkit-box-orient: vertical;
							overflow: hidden;
							text-overflow: ellipsis;
							&.animate {
								animation: fadeIn 0.7s ease-out 0.45s forwards;
							}
						}

						&.good .tag {
							background: #2d8f4e;
						}
						&.bad .tag {
							background: #c0392b;
						}
					}

					.ts-link {
						display: inline-flex;
						align-items: center;
						justify-content: center;
						gap: 8px;
						align-self: flex-end;
						padding-top: 24px;
						font-size: 18px;
						line-height: 26px;
						color: #333333;
						cursor: pointer;
						margin: 30px auto 0;

						&:hover {
							opacity: 0.8;
						}

						.arrow {
							width: 26px;
							height: 26px;
							flex-shrink: 0;
						}
					}
				}
			}
		}
	}

	&--en {
		.chart-title {
			font-size: 14px !important;
			line-height: 18px;
		}

		.tag {
			font-size: 11px !important;
		}

		.ts-date {
			.top {
				.day {
					font-size: 16px;
					line-height: 22px;
				}

				.week {
					margin-top: 4px;
					font-size: 16px;
					line-height: 22px;
					color: #666666;
				}
			}
		}

		.ts-link {
			font-size: 14px !important;

			.arrow {
				width: 18px !important;
				height: 18px !important;
				flex-shrink: 0;
			}
		}

		.box2 {
			/* 英文已登录：仅有 .box2 */
			padding-top: 34px !important;

			/* 英文未登录：同时有 .box2.box2--guest，优先级更高 */
			&--guest {
				padding-top: 0 !important;
			}
		}

		.energy-categories {
			font-size: 15px !important;
		}

		.energy-detail-link {
			font-size: 14px !important;
			line-height: 22px;

			.guide-arrow {
				width: 18px !important;
				height: 18px !important;
			}
		}
	}
}
</style>
